import { Link } from "react-router-dom";

/**
 * Stamped logo for StudyPoland — keeps the original 3-line block aesthetic
 * (STUDY / [POLAND] / .INFO) using Polish red, with a compact navbar lockup
 * variant that pairs a small red stamp with a serif wordmark.
 *
 * Props:
 *   - variant: "dark" (light bg) | "light" (dark bg)
 *   - size:    "compact" (default, navbar) | "stacked" (full 3-line stamp)
 */

const RED = "#D7141A";
const RED_DARK = "#B5111A";

function StackedStamp({ size = 1 }) {
  const w = 96 * size;
  const h = 100 * size;
  return (
    <svg
      width={w}
      height={h}
      viewBox="0 0 96 100"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="StudyPoland.info"
    >
      {/* STUDY top */}
      <text
        x="48"
        y="22"
        textAnchor="middle"
        fontFamily="Inter, Helvetica, Arial Black, sans-serif"
        fontWeight="900"
        fontSize="20"
        letterSpacing="3"
        fill={RED}
      >
        STUDY
      </text>
      {/* POLAND on filled red box */}
      <rect x="2" y="32" width="92" height="36" rx="2" fill={RED} />
      <text
        x="48"
        y="58"
        textAnchor="middle"
        fontFamily="Inter, Helvetica, Arial Black, sans-serif"
        fontWeight="900"
        fontSize="26"
        letterSpacing="1.5"
        fill="#FFFFFF"
      >
        POLAND
      </text>
      {/* .INFO bottom */}
      <text
        x="48"
        y="92"
        textAnchor="middle"
        fontFamily="Inter, Helvetica, Arial Black, sans-serif"
        fontWeight="900"
        fontSize="14"
        letterSpacing="3"
        fill={RED}
      >
        .INFO
      </text>
    </svg>
  );
}

function CompactStamp() {
  // Tiny stamped square mark used in the navbar.
  return (
    <span
      aria-hidden
      className="relative flex items-center justify-center w-10 h-10 rounded-md overflow-hidden transition-transform duration-300 group-hover:rotate-[-3deg] shrink-0"
      style={{ backgroundColor: RED }}
    >
      <span
        className="font-mono leading-none"
        style={{
          color: "#FFFFFF",
          fontWeight: 900,
          fontSize: "16px",
          letterSpacing: "0.05em",
        }}
      >
        SP
      </span>
      <span
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-1.5"
        style={{ backgroundColor: RED_DARK }}
      />
    </span>
  );
}

export default function Logo({ className = "", variant = "dark", size = "compact" }) {
  const ink = variant === "dark" ? "#0D1B2A" : "#F8F7F4";
  const subtle = variant === "dark" ? "#718096" : "#C8C3B3";

  if (size === "stacked") {
    return (
      <Link to="/" className={`inline-flex items-center ${className}`} data-testid="brand-logo-stacked">
        <StackedStamp size={1} />
      </Link>
    );
  }

  return (
    <Link
      to="/"
      className={`inline-flex items-center gap-3 group ${className}`}
      data-testid="brand-logo"
    >
      <CompactStamp />
      <span className="flex flex-col leading-none">
        <span
          className="font-serif text-[1.15rem] tracking-tight"
          style={{ color: ink, lineHeight: 1 }}
        >
          Study<span style={{ color: RED }}>Poland</span>
        </span>
        <span
          className="font-mono text-[10px] mt-1 tracking-[0.2em] uppercase"
          style={{ color: subtle }}
        >
          .info · Poland expertise
        </span>
      </span>
    </Link>
  );
}
